// De bewijslaag op het scherm (methode v3, pres-web 2026-09-30).
//
// Drie vragen die een bestuurder aan een bron stelt — wie zegt het, waarop,
// en telt het mee — als drie kleine, herbruikbare stukjes. Plus het balkje
// dat per trend laat zien hoe ver de opstellers samen boven de drempel
// staan. Alles bouwt op de bestaande bouwstenen (ReasonBadge, ScoreCell-track
// 32×3, tokens.ts); er komt geen nieuwe kleur of maat bij.
//
// Fallback is de regel: draagt de API de velden niet (methode v3 niet
// beschikbaar), dan renderen deze stukjes niets en tonen de schermen exact
// wat ze vóór deze wijziging toonden. Live mag hier niets van merken.

import type { CSSProperties } from "react";

import type { TrendBewijs } from "../contracts";
import { stempelTekst } from "../lib/schermen";
// Kleuren via de chattokens, zie SourceDetail (testfeedback 03-11, rij 6).
import { color, weight } from "./chatTokens";
import { ReasonBadge } from "./ui";

/** De optionele velden die elke bewijsregel (trend/skill-bewijs, citatie,
 *  bronchip, bron) in methode v3 draagt. */
export type BewijsBron = {
  wie?: "instituut" | "vakmedia" | "bedrijf" | null;
  uitgeverstype?: string | null;
  uitgeverstypeLabel?: string | null;
  onderbouwing?: string | null;
  onderbouwingLabel?: string | null;
  punten?: number | null;
  /** Besluit K2 (owner 06-10): het document staat achter een inlog of
   *  betaalmuur en telt niet mee in de weging; het citaat blijft zichtbaar. */
  achterBetaalmuur?: boolean;
  betaalmuurLabel?: string | null;
};

export const WIE_LABEL: Record<string, string> = {
  instituut: "✓ instituut",
  vakmedia: "vakblad",
  bedrijf: "bedrijf",
};

const WIE_TONE: Record<string, "neutral" | "amber" | "blue"> = {
  instituut: "blue",
  vakmedia: "amber",
  bedrijf: "neutral",
};

/** Het wie-woord zonder vinkje, voor lopende tekst en tellingen. */
export const WIE_WOORD: Record<string, string> = {
  instituut: "instituut",
  vakmedia: "vakblad",
  bedrijf: "bedrijf",
};

const WIE_MEERVOUD: Record<string, string> = {
  instituut: "instituten",
  vakmedia: "vakbladen",
  bedrijf: "bedrijven",
};

/** Kort woord voor het 26×20-vakje in de popovers (was: het gezagscijfer). */
export const WIE_KORT: Record<string, string> = {
  instituut: "INST",
  vakmedia: "VAK",
  bedrijf: "BEDR",
};

export const ONDERBOUWING_LABEL: Record<string, string> = {
  eigen_onderzoek: "eigen onderzoek",
  cijfers_derden: "cijfers van anderen",
  casus_anekdote: "ervaring of casus",
  bewering: "mening",
  wettekst: "wettekst",
  onduidelijk: "onbekend",
};

/** Draagt deze rij de v3-velden? (null telt als "geleverd, maar leeg".) */
export function heeftBewijsvelden(b: BewijsBron | null | undefined): boolean {
  if (!b) return false;
  return b.wie !== undefined || b.onderbouwing !== undefined || b.onderbouwingLabel !== undefined;
}

export function onderbouwingLabelVan(b: BewijsBron): string | null {
  if (b.onderbouwingLabel) return b.onderbouwingLabel;
  if (b.onderbouwing) return ONDERBOUWING_LABEL[b.onderbouwing] ?? b.onderbouwing;
  return null;
}

/** "✓ instituut" (blauw) · "vakblad" (amber) · "bedrijf" (neutraal). */
export function WiePil({ bron, title }: { bron: BewijsBron; title?: string }) {
  if (!bron.wie) return null;
  const uitleg =
    title ??
    (bron.uitgeverstypeLabel
      ? `Opsteller: ${bron.uitgeverstypeLabel}.`
      : bron.wie === "instituut"
        ? "Opsteller: een instituut (onderwijs, arbeidsmarktdata, beleid, onderzoek of regionaal)."
        : bron.wie === "vakmedia"
          ? "Opsteller: een vakblad of vakmedium."
          : "Opsteller: een bedrijf.");
  return <ReasonBadge text={WIE_LABEL[bron.wie] ?? bron.wie} tone={WIE_TONE[bron.wie] ?? "neutral"} title={uitleg} />;
}

/** Waarop de bron het baseert, als neutrale pil. */
export function WaaropPil({ bron }: { bron: BewijsBron }) {
  const label = onderbouwingLabelVan(bron);
  if (!label) return null;
  return <ReasonBadge text={label} title={`Waarop deze bron het baseert: ${label}.`} />;
}

/** "2 pt" / "1 pt" / "telt niet", 11px faint. Niets buiten trendcontext (null). */
export function PuntenTekst({ punten }: { punten: number | null | undefined }) {
  if (punten === null || punten === undefined) return null;
  return (
    <span
      title={
        punten === 0
          ? "Deze bron telt niet mee voor de bevestiging van deze trend."
          : `Deze bron weegt ${punten} ${punten === 1 ? "punt" : "punten"} mee voor deze trend (drempel 3).`
      }
      style={{ fontSize: 11, color: color.faint, whiteSpace: "nowrap" }}
    >
      {punten === 0 ? "telt niet" : `${punten} pt`}
    </span>
  );
}

/** Het betaalmuurlabel (besluit K2, reparatie 06-10): amber, zichtbaar bij
 *  elk citaat uit een afgeschermd document. Niets als het veld ontbreekt. */
export function BetaalmuurPil({ bron }: { bron: Pick<BewijsBron, "achterBetaalmuur" | "betaalmuurLabel"> | null | undefined }) {
  if (!bron?.achterBetaalmuur) return null;
  const label = bron.betaalmuurLabel || "achter een betaalmuur";
  return (
    <span data-testid="betaalmuur" style={{ display: "inline-flex" }}>
      <ReasonBadge
        text={label}
        tone="amber"
        title={`${label}: dit document staat achter een inlog of betaalmuur. Het citaat blijft zichtbaar, maar telt niet mee in de weging tot de beheerder het toestaat.`}
      />
    </span>
  );
}

/** De complete bronregel-toevoeging: wie + waarop + punten (+ betaalmuur).
 *  Niets als de velden ontbreken — dan toont de aanroeper zijn oude regel. */
export function Bronregel({ bron }: { bron: BewijsBron }) {
  return (
    <>
      <WiePil bron={bron} />
      <WaaropPil bron={bron} />
      <PuntenTekst punten={bron.punten} />
      <BetaalmuurPil bron={bron} />
    </>
  );
}

export const MIST_LABEL: Record<string, string> = {
  werkvloerbewijs: "werkvloerbewijs",
  cijfers: "cijfers",
  "tweede opsteller": "tweede opsteller",
};

export function mistTekst(bewijs: TrendBewijs): string | null {
  if (!bewijs.mist || bewijs.mist.length === 0) return null;
  return `mist: ${bewijs.mist.map((m) => MIST_LABEL[m] ?? m).join(", ")}`;
}

/** De tooltiptekst bij het bewijs: de badgetekst plus wat er mist. */
export function bewijsTitel(bewijs: TrendBewijs): string {
  const mist = mistTekst(bewijs);
  const basis = mist ? `${bewijs.tekst} · ${mist}` : bewijs.tekst;
  // W5 (bouwrun R1): bij een geldende wet de uitlegzin vooraan.
  return bewijs.verplichting && bewijs.verplichtingUitleg
    ? `bevestigd (verplichting): ${bewijs.verplichtingUitleg} · ${basis}`
    : basis;
}

/**
 * De bewijsbalk: per opsteller een blokje (22 px = 2 punten, 11 px = 1,
 * gestreept = tijdbonus, gestippeld = telt niet), 6 px hoog, radius 99,
 * accentGreen; met "mist: …" erachter in 11px faint. `klein` = 3 px hoog,
 * voor lijsten en de feed.
 */
export function Bewijsbalk({
  bewijs,
  klein = false,
  zonderMist = false,
  style,
}: {
  bewijs: TrendBewijs;
  klein?: boolean;
  zonderMist?: boolean;
  style?: CSSProperties;
}) {
  const h = klein ? 3 : 6;
  const mist = zonderMist ? null : mistTekst(bewijs);
  return (
    <span
      title={bewijsTitel(bewijs)}
      style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", maxWidth: "100%", gap: klein ? 4 : 6, ...style }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
        {bewijs.blokken.map((blok, i) => {
          const breedte = blok.punten >= 2 ? 22 : 11;
          const teltNiet = blok.punten === 0;
          return (
            <span
              key={`${blok.opsteller}-${i}`}
              title={`${blok.opsteller}: ${teltNiet ? "telt niet" : `${blok.punten} ${blok.punten === 1 ? "punt" : "punten"}`}${blok.tijd ? " · tijdbonus" : ""}`}
              style={{
                display: "inline-block",
                width: breedte,
                height: h,
                borderRadius: 99,
                boxSizing: "border-box",
                ...(teltNiet
                  ? { border: `1px dotted ${color.accentGreen}`, background: "transparent" }
                  : blok.tijd
                    ? {
                        backgroundImage: `repeating-linear-gradient(135deg, ${color.accentGreen} 0 2px, transparent 2px 4px)`,
                        backgroundColor: "transparent",
                      }
                    : { background: color.accentGreen }),
              }}
            />
          );
        })}
      </span>
      {mist && <span style={{ fontSize: 11, color: color.faint, overflowWrap: "anywhere" }}>{mist}</span>}
    </span>
  );
}

/** De badge "bevestigd door: …" (ReasonBadge tone blue), in plaats van de
 *  gezag-gebaseerde uitlichtbadge zodra er bewijs is. */
export function BewijsBadge({ bewijs }: { bewijs: TrendBewijs }) {
  return <ReasonBadge text={stempelTekst(bewijs)} tone={bewijs.laag === "bevestigd" ? "blue" : "neutral"} title={bewijsTitel(bewijs)} />;
}

/** Het ScoreCell-idioom (32×3-spoor) voor een trendlijst: vulling punten/drempel,
 *  maximaal 1, plus het aantal bronnen ernaast. */
export function BewijsCel({ bewijs, bronnen }: { bewijs: TrendBewijs; bronnen: number }) {
  const drempel = bewijs.drempel > 0 ? bewijs.drempel : 3;
  const vulling = Math.max(0, Math.min(1, bewijs.punten / drempel));
  return (
    <div title={bewijsTitel(bewijs)} style={{ display: "flex", alignItems: "center", gap: 3 }}>
      <div style={{ width: 32, height: 3, background: color.lineSoft, borderRadius: 99 }}>
        <div
          style={{
            height: "100%",
            width: `${vulling * 100}%`,
            background: color.accentGreen,
            borderRadius: 99,
          }}
        />
      </div>
      <span style={{ fontSize: 11, fontWeight: weight.medium, color: color.body }}>{bronnen}</span>
    </div>
  );
}

/** Een gezag-gebaseerde uitlichtbadge ("zwaar rapport (autoriteit 9.0)"): die
 *  wijkt voor de bewijsbadge zodra er bewijs is. */
export function isGezagBadge(tekst: string): boolean {
  return /autoriteit|gezag|zwaar rapport/i.test(tekst);
}

/** Telling per wie over een lijst bronnen: "2 instituten · 1 vakblad". */
export function wieTelling(bronnen: readonly BewijsBron[]): string | null {
  const tel: Record<string, number> = {};
  for (const b of bronnen) if (b.wie) tel[b.wie] = (tel[b.wie] ?? 0) + 1;
  const delen = (["instituut", "vakmedia", "bedrijf"] as const)
    .filter((w) => tel[w])
    .map((w) => `${tel[w]} ${tel[w] === 1 ? WIE_WOORD[w] : WIE_MEERVOUD[w]}`);
  return delen.length ? delen.join(" · ") : null;
}

/** "✓ instituut ×2 · vakblad ×1" — de korte vorm voor de bewijsregel op een kaart. */
export function wieMaal(bronnen: readonly BewijsBron[]): string | null {
  const tel: Record<string, number> = {};
  for (const b of bronnen) if (b.wie) tel[b.wie] = (tel[b.wie] ?? 0) + 1;
  const delen = (["instituut", "vakmedia", "bedrijf"] as const)
    .filter((w) => tel[w])
    .map((w) => `${WIE_LABEL[w]} ×${tel[w]}`);
  return delen.length ? delen.join(" · ") : null;
}

/** Uitgeverstype -> wie, dezelfde regel als de leeslaag (bewijskracht.py):
 *  media is een vakblad, bedrijf een bedrijf, de rest een instituut. */
export function wieVanUitgeverstype(type: string): "instituut" | "vakmedia" | "bedrijf" | null {
  if (!type) return null;
  if (type === "media") return "vakmedia";
  if (type === "bedrijf") return "bedrijf";
  if (["onderwijs", "arbeidsmarktdata", "beleid", "onderzoek", "regionaal"].includes(type)) return "instituut";
  return null;
}

/** De telling per wie uit het uitgeverstype-facet van de bronnenlijst:
 *  [["instituut", 553, "instituten"], ["vakmedia", 71, "vakbladen"], …],
 *  alleen wie voorkomt. `alleen` beperkt tot de gekozen uitgeverstypen (het
 *  facet telt zijn eigen as niet mee). */
export function wieTellingUitFacet(
  facet: readonly { value: string; count: number }[] | undefined,
  alleen: readonly string[] = [],
): { wie: string; aantal: number; woord: string }[] {
  const tel: Record<string, number> = {};
  for (const rij of facet ?? []) {
    if (alleen.length && !alleen.includes(rij.value)) continue;
    const wie = wieVanUitgeverstype(rij.value);
    if (wie) tel[wie] = (tel[wie] ?? 0) + rij.count;
  }
  return (["instituut", "vakmedia", "bedrijf"] as const)
    .filter((w) => tel[w])
    .map((w) => ({ wie: w, aantal: tel[w]!, woord: tel[w] === 1 ? WIE_WOORD[w]! : WIE_MEERVOUD[w]! }));
}
